import React, { useState, useEffect, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  Button, VStack, HStack, Text, Box, IconButton, Select, Input, FormControl, FormLabel,
  useToast, Badge, Divider, Spinner, Flex, Textarea, SimpleGrid, Alert, AlertIcon, Tooltip,
} from '@chakra-ui/react';
import { DownloadIcon, AttachmentIcon, WarningIcon } from '@chakra-ui/icons';
import { MdBlock } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../redux/store';
import {
  TramiteOP, DocumentoOP,
  subirDocumentoOP, actualizarEstatusDocumentoOP, inactivarDocumentoOP, descargarDocumentoOP,
} from '../../../../api/opTramiteApi';
import axiosInstance from '../../../../api/AxiosInstance';

const ESTATUS_DOC_COLORS: Record<string, string> = {
  PENDIENTE: 'gray', COMPLETO: 'green', INCOMPLETO: 'orange', OBSERVADO: 'yellow', RECHAZADO: 'red',
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  tramite: TramiteOP;
  onSuccess: () => void;
}

const ModalDocumentosOP: React.FC<Props> = ({ isOpen, onClose, tramite, onSuccess }) => {
  const [documentos, setDocumentos] = useState<DocumentoOP[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [descripcion, setDescripcion] = useState('');
  const [editandoEstatus, setEditandoEstatus] = useState<{ id: number; estatus: string; observacion: string } | null>(null);
  const [savingEstatus, setSavingEstatus] = useState(false);
  const [descargandoId, setDescargandoId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const user = useSelector((state: RootState) => state.auth.user);

  const cargar = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/op/tramites/${tramite.id}`);
      setDocumentos((res.data.documentos || []).filter((d: DocumentoOP) => d.activo !== false));
    } catch {
      toast({ title: 'Error al cargar documentos', status: 'error', duration: 3000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) { cargar(); setSelectedFile(null); setDescripcion(''); setEditandoEstatus(null); }
  }, [isOpen]);

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      await subirDocumentoOP(tramite.id, selectedFile, { descripcion, usuario: user?.name });
      toast({ title: 'Documento subido', status: 'success', duration: 2000 });
      setSelectedFile(null);
      setDescripcion('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      cargar();
      onSuccess();
    } catch (err: any) {
      toast({ title: 'Error al subir', description: err.message, status: 'error', duration: 3000 });
    } finally {
      setUploading(false);
    }
  };

  const handleGuardarEstatus = async () => {
    if (!editandoEstatus) return;
    if (['OBSERVADO', 'RECHAZADO'].includes(editandoEstatus.estatus) && !editandoEstatus.observacion.trim()) {
      toast({ title: 'Se requiere una observación para ese estatus', status: 'warning', duration: 2500 });
      return;
    }
    setSavingEstatus(true);
    try {
      await actualizarEstatusDocumentoOP(tramite.id, editandoEstatus.id, editandoEstatus.estatus, editandoEstatus.observacion, user?.name);
      toast({ title: 'Estatus actualizado', status: 'success', duration: 2000 });
      setEditandoEstatus(null);
      cargar();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 3000 });
    } finally {
      setSavingEstatus(false);
    }
  };

  const handleInactivar = async (doc: DocumentoOP) => {
    if (!window.confirm(`¿Marcar como inactivo "${doc.nombreArchivo}"? No se eliminará físicamente.`)) return;
    try {
      await inactivarDocumentoOP(tramite.id, doc.id, user?.name);
      toast({ title: 'Documento inactivado', status: 'info', duration: 2000 });
      cargar();
      onSuccess();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 3000 });
    }
  };

  const handleDescargar = async (doc: DocumentoOP) => {
    setDescargandoId(doc.id);
    try {
      await descargarDocumentoOP(tramite.id, doc.id, doc.nombreArchivo);
    } catch (err: any) {
      toast({
        title: 'Error al descargar documento',
        description: err?.response?.data?.error || err.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setDescargandoId(null);
    }
  };

  const requisitosDelTipo = tramite.tipoTramite?.requisitos || [];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg="#5b182b" color="white" borderTopRadius="md">
          Documentos — {tramite.folio}
        </ModalHeader>
        <ModalCloseButton color="white" />

        <ModalBody pt={4}>
          <VStack spacing={4} align="stretch">
            {/* Subir nuevo documento */}
            <Box bg="blue.50" p={4} borderRadius="md">
              <Text fontWeight="bold" mb={3} fontSize="sm">Subir Documento</Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                <FormControl>
                  <FormLabel fontSize="xs">Descripción (opcional)</FormLabel>
                  <Input size="sm" value={descripcion} onChange={e => setDescripcion(e.target.value)} placeholder="Ej. INE del solicitante" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs">Archivo</FormLabel>
                  <Input size="sm" type="file" ref={fileInputRef} onChange={e => setSelectedFile(e.target.files?.[0] || null)}
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
                </FormControl>
              </SimpleGrid>
              <Text fontSize="xs" color="gray.500" mt={1}>PDF, JPG, PNG, DOC, DOCX — máx. 10 MB</Text>
              <Button mt={3} size="sm" colorScheme="blue" leftIcon={<AttachmentIcon />}
                onClick={handleUpload} isLoading={uploading} isDisabled={!selectedFile}>
                Subir
              </Button>
            </Box>

            {/* Requisitos del tipo de trámite */}
            {requisitosDelTipo.length > 0 && (
              <Box>
                <Text fontWeight="bold" fontSize="sm" mb={2}>Requisitos del trámite</Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={1}>
                  {requisitosDelTipo.map((r: any) => {
                    const cumplido = documentos.some(d => d.idRequisito === r.id && d.estatus === 'COMPLETO');
                    return (
                      <HStack key={r.id} spacing={2}>
                        <Badge colorScheme={cumplido ? 'green' : r.obligatorio ? 'red' : 'gray'} fontSize="xs">
                          {cumplido ? '✓' : r.obligatorio ? '!' : '○'}
                        </Badge>
                        <Text fontSize="xs" color={cumplido ? 'green.600' : 'gray.700'}>
                          {r.nombreRequisito}{r.obligatorio ? ' *' : ''}
                        </Text>
                      </HStack>
                    );
                  })}
                </SimpleGrid>
                <Text fontSize="xs" color="red.500" mt={1}>* Obligatorio</Text>
              </Box>
            )}

            <Divider />

            {/* Lista de documentos */}
            <Box>
              <Text fontWeight="bold" fontSize="sm" mb={2}>Documentos Adjuntos ({documentos.length})</Text>

              {loading ? (
                <Flex justify="center" p={6}><Spinner color="red.500" /></Flex>
              ) : documentos.length === 0 ? (
                <Text color="gray.400" textAlign="center" py={4} fontSize="sm">No hay documentos adjuntos</Text>
              ) : (
                <VStack spacing={2} align="stretch">
                  {documentos.map(doc => (
                    <Box key={doc.id} p={3} bg="gray.50" borderRadius="md" borderWidth="1px">
                      <Flex justify="space-between" align="start" gap={2} flexWrap="wrap">
                        <Box flex={1} minW="200px">
                          <HStack spacing={2} mb={1} flexWrap="wrap">
                            <Text fontWeight="semibold" fontSize="sm">{doc.nombreArchivo}</Text>
                            <Badge colorScheme={ESTATUS_DOC_COLORS[doc.estatus] || 'gray'} fontSize="xs">
                              {doc.estatus}
                            </Badge>
                            {doc.esSolicitud && <Badge colorScheme="purple" fontSize="xs">Solicitud</Badge>}
                          </HStack>
                          {doc.descripcion && <Text fontSize="xs" color="gray.500">{doc.descripcion}</Text>}
                          {doc.requisito && <Text fontSize="xs" color="blue.600">📎 {doc.requisito.nombreRequisito}</Text>}
                          {doc.observacion && (
                            <Alert status={doc.estatus === 'RECHAZADO' ? 'error' : 'warning'} py={1} px={2} borderRadius="md" mt={1}>
                              <AlertIcon boxSize={3} />
                              <Text fontSize="xs">{doc.observacion}</Text>
                            </Alert>
                          )}
                          <Text fontSize="xs" color="gray.400" mt={1}>
                            Subido: {new Date(doc.fechaSubida).toLocaleString('es-MX')}{doc.usuarioSubida ? ` · ${doc.usuarioSubida}` : ''}
                          </Text>
                        </Box>
                        <HStack spacing={1} flexShrink={0}>
                          <Tooltip label="Descargar">
                            <IconButton aria-label="descargar" icon={<DownloadIcon />} size="xs" colorScheme="blue" variant="ghost"
                              isLoading={descargandoId === doc.id}
                              onClick={() => handleDescargar(doc)} />
                          </Tooltip>
                          <Tooltip label="Cambiar estatus">
                            <Button size="xs" variant="outline" colorScheme="orange"
                              onClick={() => setEditandoEstatus({ id: doc.id, estatus: doc.estatus, observacion: doc.observacion || '' })}>
                              Estatus
                            </Button>
                          </Tooltip>
                          <Tooltip label="Inactivar (no elimina)">
                            <IconButton aria-label="inactivar" icon={<MdBlock />} size="xs" colorScheme="red" variant="ghost"
                              onClick={() => handleInactivar(doc)} />
                          </Tooltip>
                        </HStack>
                      </Flex>

                      {/* Panel inline de edición de estatus */}
                      {editandoEstatus?.id === doc.id && (
                        <Box mt={2} p={3} bg="white" borderRadius="md" borderWidth="1px" borderColor="orange.200">
                          <Text fontSize="xs" fontWeight="bold" mb={2}>Actualizar estatus del documento</Text>
                          <SimpleGrid columns={2} spacing={2}>
                            <FormControl>
                              <FormLabel fontSize="xs">Nuevo Estatus</FormLabel>
                              <Select size="sm" value={editandoEstatus.estatus}
                                onChange={e => setEditandoEstatus({ ...editandoEstatus, estatus: e.target.value })}>
                                <option value="PENDIENTE">Pendiente</option>
                                <option value="COMPLETO">Completo</option>
                                <option value="INCOMPLETO">Incompleto</option>
                                <option value="OBSERVADO">Observado</option>
                                <option value="RECHAZADO">Rechazado</option>
                              </Select>
                            </FormControl>
                          </SimpleGrid>
                          {['OBSERVADO', 'RECHAZADO'].includes(editandoEstatus.estatus) && (
                            <FormControl mt={2} isRequired>
                              <FormLabel fontSize="xs">Observación / Motivo <WarningIcon color="red.400" boxSize={3} /></FormLabel>
                              <Textarea size="sm" rows={2} value={editandoEstatus.observacion}
                                onChange={e => setEditandoEstatus({ ...editandoEstatus, observacion: e.target.value })}
                                placeholder="Describa el motivo de la observación o rechazo..." />
                            </FormControl>
                          )}
                          <HStack mt={2} spacing={2}>
                            <Button size="xs" colorScheme="orange" onClick={handleGuardarEstatus} isLoading={savingEstatus}>
                              Guardar
                            </Button>
                            <Button size="xs" variant="ghost" onClick={() => setEditandoEstatus(null)}>Cancelar</Button>
                          </HStack>
                        </Box>
                      )}
                    </Box>
                  ))}
                </VStack>
              )}
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px solid #eee">
          <Button variant="ghost" onClick={onClose}>Cerrar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalDocumentosOP;
